import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";
import { AuthEnums, CommonEnums } from "models/enums";
import { DocumentDraftFormTypes, DocumentFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import useAPIClientHelper from "./useAPIClientHelper";

function useDocumentDraftService() {
  const { apiClientInstance } = useAPIClientHelper();

  const get = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .DocumentDraftClient.GetById(id)
      .then((res) => res.data);
  };

  const filter = (filter: WintSuperkollAPIModel.DocumentDraftFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).DocumentDraftClient.GetByFilter(filter);
  };

  const reportFilter = (baseFilter: WintUtilsWebApi.Filter, formFilter?: DocumentDraftFormTypes.Filter) => {
    return filter({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
    });
  };

  const reportToAPIFilter = (
    formFilter?: DocumentDraftFormTypes.Filter
  ): Omit<WintSuperkollAPIModel.DocumentDraftFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      sentDateFrom: formFilter?.date?.start,
      sentDateTo: formFilter?.date?.end,
      states: formFilter?.state?.map((s) => s.id as WintSuperkollAPIModel.DocumentDraftState),
      types: formFilter?.type?.map((s) => s.id as WintSuperkollAPIModel.DocumentDraftType),
      sources: formFilter?.source?.map((s) => s.id as WintSuperkollAPIModel.DocumentDraftSource),
      maxAmount: formFilter?.amount?.highest,
      minAmount: formFilter?.amount?.lowest,
      from: formFilter?.from,
      subject: formFilter?.subject,
    };
  };

  const getDocumentDraftStatuses = (): AutocompleteListItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.DocumentDraftState).map((key) => {
      return {
        id: Number(key),
        value: LocalizationHelperInstance.getFormattedMessage(`DocumentDraft.State_${key}`),
      };
    });
  };

  const getDocumentDraftTypes = (): AutocompleteListItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.DocumentDraftType)
      .filter((d) => d !== WintSuperkollAPIModel.DocumentDraftType.MissingDocument)
      .map((key) => {
        return {
          id: Number(key),
          value: getTypeText(key),
        };
      });
  };

  const getDocumentDraftSources = () =>
    EnumHelper.getNumericItemValues(WintSuperkollAPIModel.DocumentDraftSource).map((key) => {
      return {
        id: Number(key),
        value: getSourceText(key),
      };
    });

  const getStateText = (state: WintSuperkollAPIModel.DocumentDraftState) => {
    return LocalizationHelperInstance.getFormattedMessage(`DocumentDraft.State_${state}`);
  };

  const getTypeText = (type: WintSuperkollAPIModel.DocumentDraftType) => {
    return LocalizationHelperInstance.getFormattedMessage(`DocumentDraft.Type_${type}`);
  };

  const getSourceText = (source: WintSuperkollAPIModel.DocumentDraftSource) => {
    return LocalizationHelperInstance.getFormattedMessage(`DocumentDraft.Source_${source}`);
  };

  const getStates = () =>
    EnumHelper.getNumericItemValues(WintSuperkollAPIModel.DocumentDraftState).map((key) => {
      return {
        id: Number(key),
        value: getStateText(key),
      };
    });

  const getDocumentUrl = (documentDraft: WintSuperkollAPIModel.DocumentDraft) => {
    const id = documentDraft.entityId;
    switch (documentDraft.type) {
      case WintSuperkollAPIModel.DocumentDraftType.IncomingInvoice:
        return `/IncomingInvoice/${id}`;
      case WintSuperkollAPIModel.DocumentDraftType.Receipt:
        return `/Receipt/${id}`;
      case WintSuperkollAPIModel.DocumentDraftType.GenericDocument:
        return `/AccountingDocument/${id}`;
      case WintSuperkollAPIModel.DocumentDraftType.SelfInvoice:
        return `/SelfInvoice/${documentDraft.entityGuid}`;
      default:
        return "";
    }
  };

  const documentTypeToDocumentDraftType = (type: CommonEnums.DocumentTypes) => {
    switch (type) {
      case CommonEnums.DocumentTypes.IncomingInvoice:
        return WintSuperkollAPIModel.DocumentDraftType.IncomingInvoice;
      case CommonEnums.DocumentTypes.Receipt:
        return WintSuperkollAPIModel.DocumentDraftType.Receipt;
      default:
        return WintSuperkollAPIModel.DocumentDraftType.GenericDocument;
    }
  };

  const post = (documentDraftPost: WintSuperkollAPIModel.DocumentDraftPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).DocumentDraftClient.Post(
      documentDraftPost
    );
  };

  const deleteDocumentDraft = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).DocumentDraftClient.Delete(id);
  };

  const formToReceiptPost = (values: DocumentFormTypes.CreateNew): WintSuperkollAPIModel.ReceiptPost => {
    return {
      dateTime: values.receipt.date ?? new Date(),
      amount: values.receipt.amount ?? 0,
      currency: values.receipt.currency,
      receiptCategoryId: values.receipt.category ?? 0,
      paymentMethodId: values.receipt.paymentMethod ?? undefined,
      paymentMethodType: 0,
      intent:
        values.receipt.categoryType === WintSuperkollAPIModel.ReceiptCategoryType.Other
          ? values.receipt.otherExplanation
          : values.receipt.purpose,
      dimensionList: values.receipt.dimensions,
      attachments: values.filesToUpload as WintSuperkollAPIModel.ReceiptAttachment[],
      persons:
        values.receipt.categoryType === WintSuperkollAPIModel.ReceiptCategoryType.Representation ||
        values.receipt.categoryType === WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation
          ? values.receipt.persons?.map((p) => ({
              id: 0,
              name: p.name,
              company: p.company,
            }))
          : [],
      receiptPaymentInfo: !values.receipt.canBePayedOut
        ? {
            personNumber: values.receipt.personNumber,
            clearingNumber: values.receipt.clearingNumber,
            bankAccount: values.receipt.bankAccount,
            hasMissingPaymentDetails: values.receipt.isManualPayment ?? false,
          }
        : undefined,
      comment: values.receipt.internalNote,
      otherPaymentMethodExplanation: values.receipt.otherPaymentMethodExplanation,
      isOtherPaymentMethod: values.receipt.isOtherPaymentMethod ?? false,
    };
  };

  const formToIncomingInvoicePost = (
    values: DocumentFormTypes.CreateNew
  ): WintSuperkollAPIModel.IncomingInvoicePost => {
    return {
      attachments: values.filesToUpload as WintSuperkollAPIModel.IncomingInvoiceAttachment[],
    };
  };

  const formToGenericDocumentPost = (
    values: DocumentFormTypes.CreateNew
  ): WintSuperkollAPIModel.GenericDocumentPost => {
    return {
      images: values.filesToUpload?.map(
        (file): WintSuperkollAPIModel.GenericDocumentImage => ({
          contentType: file.contentType,
          imageData: file.data,
          name: file.fileName,
        })
      ),
      subject: values.document.subject,
      bookingDate: values.document.bookingDate,
    };
  };

  const getAttachment = (documentDraftId: number, attachmentId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .DocumentDraftClient.GetAttachmentAsync(documentDraftId, attachmentId)
      .then((res) => res.data);
  };

  const getAttachmentData = (documentDraftId: number, attachmentId: string | number) => {
    return getAttachment(documentDraftId, attachmentId as number).then((res) => res.data ?? "");
  };

  const handleRemovedDraft = (documentDraftId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company).DocumentDraftClient.HandleRemovedDraft(documentDraftId);
  };

  return {
    get,
    filter,
    reportFilter,
    reportToAPIFilter,
    getDocumentDraftStatuses,
    getDocumentDraftTypes,
    getDocumentDraftSources,
    getStateText,
    getStates,
    getTypeText,
    getSourceText,
    getDocumentUrl,
    documentTypeToDocumentDraftType,
    post,
    deleteDocumentDraft,
    formToReceiptPost,
    formToIncomingInvoicePost,
    formToGenericDocumentPost,
    getAttachment,
    getAttachmentData,
    handleRemovedDraft,
  };
}

export default useDocumentDraftService;
